import styled from '@emotion/styled';
import ShareIcon from '@mui/icons-material/Share';
import {Button, FormControl, useMediaQuery} from '@mui/material';

import React, {useCallback, useEffect, useState, useRef, useLayoutEffect} from 'react';
import {RequestState, ShareType, SocialNetwork} from 'modules';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {getCurrentLeague} from 'modules/selectors';
import {copyToClipboard} from 'modules/utils/dom_helpers';
import {ShareUtility} from 'modules/utils/share/social_share.utility';
import i18next from 'i18next';
import {inviteToGroup} from 'modules/utils/mlbConfig';
import {Exist} from 'components/Exist';
import RallySSO from 'modules/utils/SSO/rallySSO';
import {first} from 'lodash';
import {createFanHubEvent, EventGUID} from 'modules/utils/analytics/analytics';

const mobileViewQuery = `max-width: 880px`;

const FormLabelExtended = styled.p`
	font-weight: 700;
	font-size: 16px;
	line-height: 16px;
	letter-spacing: 0.15px;
	margin-bottom: 8px;
	margin-top: 20px;
	color: #fff;
`;

const StepDescription = styled.div`
	font-weight: 400;
	font-size: 14px;
	line-height: 1.4;
	margin-top: 6px;

	b {
		font-weight: 800;
	}
`;

const ButtonMui = styled(Button)`
	margin-bottom: 0;
	text-transform: capitalize;
	display: flex;
	gap: 10px;
	max-width: 180px;
	margin-top: 16px;
	@media (${mobileViewQuery}) {
		width: 100%;
		max-width: 100%;
	}
`;

interface IProps {
	leagueId?: number;
	isActiveFirstStep?: boolean;
	withAutoFocus?: boolean;
}

const useCopy = () => {
	let timeout: ReturnType<typeof setTimeout> | null = null;

	useEffect(() => {
		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [timeout]);

	const [copyState, setCopyState] = useState(RequestState.IDLE);

	const copy = (value?: string) => {
		const DELAY_MS = 10000;
		timeout = setTimeout(() => {
			setCopyState(RequestState.IDLE);
		}, DELAY_MS);
		if (!value) {
			setCopyState(RequestState.ERROR);
			return;
		}

		setCopyState(RequestState.REQUESTED);

		void copyToClipboard(value)
			.then(() => {
				setCopyState(RequestState.RECEIVED);
			})
			.catch(() => {
				setCopyState(RequestState.ERROR);
			});
	};

	return {
		copy,
		copyState,
	};
};

const useInvites = (withAutoFocus: boolean) => {
	const i18n = useTranslation();
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const currentLeague = useSelector(getCurrentLeague);
	const isMobile = useMediaQuery(`(${mobileViewQuery})`);
	const isNativeAndNoInvite = RallySSO.IS_NATIVE && !RallySSO.nativeBridge?.inviteToGroup;

	const isSupportMobileShare = "share" in navigator;

	const {copyState, copy} = useCopy();

	const copyCodeHandler = useCallback(() => {
		if (currentLeague) {
			copy(String(currentLeague.code));
		}
	}, [copy, currentLeague]);

	const shareHandler = useCallback(() => {
		if (!currentLeague) {
			return;
		}
		createFanHubEvent(EventGUID.SHARE_IN_LEAGUES);
		inviteToGroup(currentLeague.code);
	}, [currentLeague]);

	const _shareMessage = currentLeague
		? i18n.t(
				"share.native.message",
				`Join my group! Group code: ${String(currentLeague.code)}`,
				{X: currentLeague.code}
		  )
		: "";

	const shareTw = () => {
		if (currentLeague) {
			ShareUtility.share({
				message: _shareMessage,
				type: ShareType.LEAGUE,
				leagueId: currentLeague.id,
				socialNetwork: SocialNetwork.TWITTER,
				locale: i18next.resolvedLanguage,
			});
		}
	};

	const shareFB = () => {
		if (currentLeague) {
			ShareUtility.share({
				message: _shareMessage,
				type: ShareType.LEAGUE,
				leagueId: currentLeague.id,
				socialNetwork: SocialNetwork.FACEBOOK,
				locale: i18next.resolvedLanguage,
			});
		}
	};

	const shareMobile = () => {
		if (currentLeague) {
			ShareUtility.share({
				message: _shareMessage,
				type: ShareType.LEAGUE,
				leagueId: currentLeague.id,
				socialNetwork: SocialNetwork.MOBILE,
				locale: i18next.resolvedLanguage,
			});
		}
	};

	useLayoutEffect(() => {
		const button = first(wrapperRef.current?.getElementsByTagName("button"));
		if (button && withAutoFocus) {
			button.focus();
		}
	}, [withAutoFocus]);

	return {
		i18n,
		currentLeague,
		isMobile,
		isSupportMobileShare,
		copyCodeHandler,
		copyState,
		shareMobile,
		shareFB,
		shareTw,
		shareHandler,
		isNativeAndNoInvite,
		wrapperRef,
	};
};
export const LeagueInviteForm: React.FC<IProps> = ({
	isActiveFirstStep = false,
	withAutoFocus,
}) => {
	const {
		i18n,
		currentLeague,
		copyCodeHandler,
		copyState,
		shareHandler,
		isNativeAndNoInvite,
		wrapperRef,
	} = useInvites(!!withAutoFocus);

	return (
		<div ref={wrapperRef}>
			<FormControl fullWidth>
				<FormLabelExtended>
					{i18n.t("league.invite.invite_via_code", "Invite Via Code")}
				</FormLabelExtended>
				<StepDescription>
					{i18n.t(
						"league.invite.invite_description",
						`Great, now that you have created a group, invite others to join. Your unique group code for ${String(
							currentLeague?.name || ""
						)} is:`,
						{
							X: currentLeague?.name,
						}
					)}
					<b> {currentLeague?.code}</b>
				</StepDescription>
				<Exist when={isNativeAndNoInvite}>
					<ButtonMui
						onClick={copyCodeHandler}
						variant={"contained"}
						color={"secondary"}
						aria-live={"assertive"}
					>
						{
							{
								[RequestState.IDLE]: i18n.t(
									"league.invite.copy_code",
									"Copy Invite Link"
								),
								[RequestState.RECEIVED]: i18n.t(
									"league.invite.copy_code_success",
									"Copied!"
								),
								[RequestState.ERROR]: i18n.t(
									"league.invite.copy_code_failed",
									"Failed to copy"
								),
								[RequestState.REQUESTED]: i18n.t(
									"league.invite.copy_code_in_progress",
									"Copying..."
								),
							}[copyState]
						}
					</ButtonMui>
				</Exist>
			</FormControl>
			<Exist when={!isNativeAndNoInvite}>
				<FormControl fullWidth>
					<FormLabelExtended>
						{i18n.t("league.invite.invite_via_social", "Invite Via Social")}
					</FormLabelExtended>
					<ButtonMui
						onClick={shareHandler}
						color={"secondary"}
						variant={"contained"}
						aria-live={"assertive"}
						aria-label={i18n.t("league.invite.share", "Share")}
						disabled={isActiveFirstStep}
					>
						<ShareIcon /> {i18n.t("league.invite.share", "Share")}
					</ButtonMui>
				</FormControl>
			</Exist>
		</div>
	);
};
